import { useState, type ReactNode } from "react";

interface Props {
  title: string;
  // Right-aligned summary in the header (subtotal, count) — already formatted.
  sub?: string;
  // Optional badge rendered next to the title, e.g. whether the section reaches
  // the Difference. `tone` picks the colour; the text itself comes from the API.
  badge?: string;
  badgeTone?: "in" | "out";
  hint?: string;
  defaultOpen?: boolean;
  // Controls in the header (a view switch, a button). Clicking them never folds the section.
  actions?: ReactNode;
  // Controlled open state, for a parent that must open the section itself.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Keep the body mounted (hidden) while folded, so state inside it — a grid's search,
  // filters and page — survives a fold.
  keepMounted?: boolean;
  className?: string;
  children: ReactNode;
}

// Shared collapsible section — the statement tabs and the Other tab both group rows
// into labelled, foldable blocks, and previously each had its own near-identical
// copy (only one of which actually collapsed). One component so the interaction,
// spacing and header layout stay identical everywhere.
//
// Children render only while open (unless keepMounted): the Other tab stacks several
// DataTables, and mounting a collapsed one costs a full column blank-scan + row render
// for rows nobody is looking at.
export default function CollapsibleSection({
  title, sub, badge, badgeTone = "out", hint, defaultOpen = false, actions,
  open: openProp, onOpenChange, keepMounted = false, className, children,
}: Props) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (v: boolean) => {
    setOpenState(v);
    onOpenChange?.(v);
  };
  return (
    <div className={"stmt-section" + (className ? ` ${className}` : "")}>
      {/* role/tabIndex/aria-expanded rather than a bare div+onClick: this header is the
          only way into the section, so it has to be reachable from the keyboard. */}
      <div className="stmt-section-head" onClick={() => setOpen(!open)}
        role="button" tabIndex={0} aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); }
        }}>
        <span className="stmt-section-title">
          <span>{open ? "▾" : "▸"} {title}</span>
          {badge && <span className={`diff-badge diff-badge-${badgeTone}`}>{badge}</span>}
        </span>
        {(actions || sub) && (
          <span className="stmt-section-right">
            {actions && (
              <span className="stmt-section-actions" onClick={(e) => e.stopPropagation()}
                // Only the keys the header acts on: swallowing Esc too kept it from ever
                // reaching the matching workspace this slot's button opens.
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}>
                {actions}
              </span>
            )}
            {sub && <span className="sub">{sub}</span>}
          </span>
        )}
      </div>
      {(open || keepMounted) && (
        <div className="stmt-section-body" hidden={!open}>
          {hint && <p className="muted stmt-section-hint">{hint}</p>}
          {children}
        </div>
      )}
    </div>
  );
}
